import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { Category, Item } from '../../lib/supabase'
import type { PlusReason } from '../../lib/billing'
import { FREE_ITEM_LIMIT } from '../../lib/pricing'
import { nextCategory, resolveCategoryId } from '../../lib/autocategory'
import type { CatalogEntry } from '../../lib/catalog'
import { indexItems, matchItems } from '../../lib/search'
import { userLocale } from '../../lib/locale'
import { normalizeName } from '../../lib/items'
import { itemKey } from '../../lib/normalize'
import { HAPTICS } from '../../lib/constants'
import { haptic } from '../../lib/haptics'
import { ArrowUpIcon, GridIcon, PlusIcon } from '../icons'

// Quick add (ux/03 QuickAdd): [+] [input] [category pill] [submit]. The pill
// and the submit appear once there is text; the pill shows the guess
// (household memory first, then the EN/SV keyword map) and a tap cycles to the
// next category. Enter adds, clears, keeps focus and resets the override.
//
// Typing a name that is already on the list (or in the basket) shows it
// underneath instead of adding a duplicate: tap to put it back, or edit it.
// Names from earlier trips (lib/catalog.ts) show under "From before": the plus
// adds it to the category it went in last time (with an Undo toast); the name
// opens the editor to change it first, with "Add to list". A name that is
// neither shows as "New item": its name opens the editor the same way, so an
// amount or a note can go with it.

// On Free (042_free_plus.sql) the list holds 15 open items: the last few
// places are counted under the field, a full list swaps the field for a bar
// that offers Plus, and an add the server refused anyway (made offline, say)
// stays above the field as "Not saved, list full" until it is removed or Plus
// saves it. From before is Plus: on Free its group says so instead.

// The list's own matches stay few; history gets a little more room.
const LIST_MATCHES = 5
const HISTORY_MATCHES = 6
// The count of places left shows from this many down.
const COUNT_FROM = 3

interface Props {
  items: Item[]
  /** Names used before and not on the list now. */
  catalog: CatalogEntry[]
  /** nameKey → category, from history and the list (useItemHistory). */
  memory: Map<string, string>
  categories: Category[]
  layout: 'phone' | 'desktop'
  tintFor: (categoryId: string | null) => string
  onAdd: (name: string, categoryId: string | null) => void
  onPutBack: (id: string) => void
  onEditItem: (item: Item, intent: 'add' | 'edit') => void
  /** Trial, paid or grace: From before and unlimited items. */
  plus: boolean
  /** Places left on a free list; Infinity with Plus. */
  itemsLeft: number
  /** Adds a full free list refused (useItems.unsaved). */
  unsaved: Item[]
  onDiscardUnsaved: (id: string) => void
  onUpgrade: (reason: PlusReason) => void
  /** The plus on a "From before" row: on the list at once, with Undo (GroceryList). */
  onAddFromBefore: (entry: CatalogEntry) => void
  /** The rest of that row, or of the typed new name: the editor, to change it before adding. */
  onOpenFromBefore: (entry: CatalogEntry) => void
  /** The grid button in the empty field: All items, to tick several at once (Plus, like From before). */
  onBrowse: () => void
}

export function QuickAdd({
  items, catalog, memory, categories, layout, tintFor, onAdd, onPutBack, onEditItem, plus, itemsLeft, unsaved,
  onDiscardUnsaved, onUpgrade, onAddFromBefore, onOpenFromBefore, onBrowse,
}: Props) {
  const [name, setName] = useState('')
  const [override, setOverride] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const matchesRef = useRef<HTMLDivElement>(null)

  const index = useMemo(() => indexItems(items), [items])
  const catalogIndex = useMemo(() => indexItems(catalog), [catalog])
  const matches = useMemo(() => matchItems(index, name, userLocale(), LIST_MATCHES), [index, name])
  const earlier = useMemo(() => matchItems(catalogIndex, name, userLocale(), HISTORY_MATCHES, { keepOrder: true }), [catalogIndex, name])
  const ordered = useMemo(() => [...categories].sort((a, b) => a.sort_order - b.sort_order), [categories])
  const guess = useMemo(() => (name.trim() ? resolveCategoryId(name, memory, categories) : null), [name, memory, categories])
  const categoryId = override ?? guess
  const category = ordered.find(c => c.id === categoryId) ?? null
  const hasText = name.trim().length > 0
  // The typed name as a row of its own, unless the list or the history already has it.
  const typedKey = itemKey(name)
  const isNew = hasText && !!typedKey && ![...matches, ...earlier].some(m => itemKey(m.name) === typedKey)

  // The phone's box reads upwards: open it at the bottom, by the field.
  useLayoutEffect(() => {
    const box = matchesRef.current
    if (box && layout === 'phone') box.scrollTop = box.scrollHeight
  }, [name, layout, matches, earlier])

  function reset() {
    setName('')
    setOverride(null)
  }

  function submit() {
    const clean = normalizeName(name)
    if (!clean) return
    onAdd(clean, categoryId)
    reset()
    inputRef.current?.focus()
  }

  function addAgain(entry: CatalogEntry) {
    onAddFromBefore(entry)
    reset()
    inputRef.current?.focus()
  }

  function cycle() {
    haptic(HAPTICS.categoryPick)
    setOverride(nextCategory(ordered.map(c => c.id), categoryId))
    inputRef.current?.focus()
  }

  const unsavedGroup = unsaved.length > 0 && (
    <div role="group" aria-labelledby="ml-unsaved-label" className="ml-matches ml-unsaved">
      <p id="ml-unsaved-label" className="ml-label ml-matches__label">Not saved, list full</p>
      <ul>
        {unsaved.map(item => (
          <li key={item.id} className="ml-match ml-match--unsaved" style={{ '--ml-tint': tintFor(item.category_id) } as CSSProperties}>
            <span className="ml-dot" aria-hidden="true" />
            <span className="ml-match__name">
              <span>{item.name}</span>
              {item.quantity && <span className="ml-match__qty">{item.quantity}</span>}
            </span>
            <button type="button" className="ml-text-btn" aria-label={`Remove ${item.name}`}
              onClick={() => onDiscardUnsaved(item.id)}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="ml-text-btn ml-unsaved__plus" onClick={() => onUpgrade('list_full')}>Get Plus</button>
    </div>
  )

  // A full free list: nothing to type until something is ticked off. The
  // server's trigger refuses the sixteenth open item regardless.
  if (itemsLeft <= 0) {
    return (
      <div className={`ml-quick-wrap ml-quick-wrap--${layout}`} data-tour="quick-add">
        {unsavedGroup}
        <div data-testid="list-full-bar" className={`ml-quick ml-quick--${layout} ml-quick--full`}>
          <div className="ml-quick__full-text">
            <p className="ml-quick__full-title">Your list is full</p>
            <p className="ml-caption">
              Free holds {FREE_ITEM_LIMIT} items at a time. Tick some off, or get Plus for unlimited items.
            </p>
          </div>
          <button type="button" className="ml-btn ml-btn--primary" onClick={() => onUpgrade('list_full')}>Get Plus</button>
        </div>
      </div>
    )
  }

  // On the phone the box sits above the field: what can be added goes nearest
  // the field (From before, then the typed name, the list's own matches on top)
  // and each group's best match sits at its bottom, the box scrolled down to
  // it. On desktop the field is on top and the box reads down.
  const phone = layout === 'phone'
  const flip = <T,>(xs: T[]) => (phone ? [...xs].reverse() : xs)
  // New item, On your list, From before (desktop's order).
  const groups = [
    isNew && (
      <div key="new" role="group" aria-labelledby="ml-new-label" className="ml-matches__group">
        <p id="ml-new-label" className="ml-label ml-matches__label">New item</p>
        <ul>
          <li className="ml-match" style={{ '--ml-tint': tintFor(categoryId) } as CSSProperties}>
            <span className="ml-dot" aria-hidden="true" />
            <button type="button" className="ml-match__name" aria-label={`Edit ${normalizeName(name)} before adding`}
              onClick={() => {
                onOpenFromBefore({ key: typedKey, name: normalizeName(name), categoryId, quantity: null })
                reset()
              }}>
              <span>{normalizeName(name)}</span>
            </button>
            <span className="ml-caption">{category?.name ?? 'Other'}</span>
          </li>
        </ul>
      </div>
    ),
    matches.length > 0 && (
      <div key="list" role="group" aria-labelledby="ml-matches-label" className="ml-matches__group">
        <p id="ml-matches-label" className="ml-label ml-matches__label">On your list</p>
        <ul>
          {flip(matches).map(item => (
            <li key={item.id} className="ml-match" style={{ '--ml-tint': tintFor(item.category_id) } as CSSProperties}>
              <span className="ml-dot" aria-hidden="true" />
              <button type="button" className="ml-match__name"
                aria-label={item.done ? `Edit ${item.name} before adding` : `Edit ${item.name}`}
                onClick={() => { onEditItem(item, item.done ? 'add' : 'edit'); reset() }}>
                <span className={item.done ? 'is-done' : undefined}>{item.name}</span>
                {item.quantity && <span className="ml-match__qty">{item.quantity}</span>}
              </button>
              {item.done
                ? (
                  <button type="button" className="ml-text-btn" aria-label={`Put back ${item.name}`}
                    onClick={() => { onPutBack(item.id); reset(); inputRef.current?.focus() }}>
                    Put back
                  </button>
                )
                : <span className="ml-caption">On the list</span>}
            </li>
          ))}
        </ul>
      </div>
    ),
    earlier.length > 0 && (!plus ? (
      <div key="earlier" role="group" aria-labelledby="ml-earlier-label" className="ml-matches__group">
        <p id="ml-earlier-label" className="ml-label ml-matches__label">From before</p>
        <p className="ml-caption ml-matches__locked">
          Plus remembers what you bought before, so it’s one tap to add again.
          {' '}
          <button type="button" className="ml-text-btn" onClick={() => onUpgrade('memory')}>Get Plus</button>
        </p>
      </div>
    ) : (
      <div key="earlier" role="group" aria-labelledby="ml-earlier-label" className="ml-matches__group">
        <p id="ml-earlier-label" className="ml-label ml-matches__label">From before</p>
        <ul>
          {flip(earlier).map(entry => (
            <li key={entry.key} className="ml-match" style={{ '--ml-tint': tintFor(entry.categoryId) } as CSSProperties}>
              <span className="ml-dot" aria-hidden="true" />
              <button type="button" className="ml-match__name" aria-label={`Edit ${entry.name} before adding`}
                onClick={() => { onOpenFromBefore(entry); reset() }}>
                <span>{entry.name}</span>
                {entry.quantity && <span className="ml-match__qty">{entry.quantity}</span>}
              </button>
              <button type="button" className="ml-match__add" aria-label={`Add ${entry.name} again`}
                onClick={() => addAgain(entry)}>
                <PlusIcon size={18} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    )),
  ]

  return (
    <div className={`ml-quick-wrap ml-quick-wrap--${layout}`} data-tour="quick-add">
      {unsavedGroup}
      {groups.some(Boolean) && <div ref={matchesRef} className="ml-matches">{phone ? [groups[1], groups[0], groups[2]] : groups}</div>}

      <div className={`ml-quick ml-quick--${layout}`}>
        <span className="ml-quick__plus" aria-hidden="true"><PlusIcon size={20} /></span>
        <input
          ref={inputRef}
          className="ml-quick__input"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
          placeholder={layout === 'phone' ? 'New item' : 'Add an item and press Enter'}
          aria-label="New item"
          enterKeyHint="done"
          autoComplete="off"
          aria-describedby={itemsLeft <= COUNT_FROM ? 'ml-places-left' : undefined}
        />
        {!hasText && (
          <button type="button" className="ml-quick__browse" aria-label="All items"
            onClick={() => (plus ? onBrowse() : onUpgrade('memory'))}>
            <GridIcon size={20} />
          </button>
        )}
        {hasText && (
          <>
            <button type="button" className="ml-pill" onClick={cycle}
              style={{ '--ml-tint': tintFor(category?.id ?? null) } as CSSProperties}
              aria-label={`Category: ${category?.name ?? 'Other'}. Change category`}>
              <span className="ml-dot" aria-hidden="true" />
              <span className="ml-pill__name">{category?.name ?? 'Other'}</span>
            </button>
            <button type="button" className="ml-quick__submit" onClick={submit} aria-label={`Add ${normalizeName(name)}`}>
              <ArrowUpIcon size={18} />
            </button>
          </>
        )}
      </div>
      {itemsLeft <= COUNT_FROM && (
        <p id="ml-places-left" className="ml-caption ml-quick__left">
          {itemsLeft} {itemsLeft === 1 ? 'place' : 'places'} left on Free
        </p>
      )}
    </div>
  )
}
